import { useEffect, useState } from "react";

interface Flag {
  name: string;
  enabled: boolean;
}

interface VariantFlag {
  name: string;
  value: string;
  variants: string[];
}

interface DevFlagsPageProps {
  apiUrl: string;
}

// The same bundle ships to prod and dev, so this page exists everywhere. What
// makes it dev-only is the API: /api/dev/* is not mounted outside dev and
// answers 404, which is rendered below as "not available here" rather than as
// an error. There is nothing to hide - the page is a switch with no backend
// behind it on prod.
export function DevFlagsPage({ apiUrl }: DevFlagsPageProps) {
  const [flags, setFlags] = useState<Flag[] | null>(null);
  const [variantFlags, setVariantFlags] = useState<VariantFlag[] | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = async () => {
    try {
      const res = await fetch(`${apiUrl}/api/dev/flags`);
      if (res.status === 404) {
        setUnavailable(true);
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setFlags(await res.json());
      setError(null);
    } catch (err) {
      setError(`Could not load flags: ${err}`);
    }
  };

  const loadVariants = async () => {
    try {
      const res = await fetch(`${apiUrl}/api/dev/variant-flags`);
      // Not a separate "unavailable" branch -- /api/dev/flags above already
      // makes that call for the page as a whole, and both endpoints are
      // mounted or unmounted together.
      if (!res.ok) return;
      setVariantFlags(await res.json());
    } catch {
      // Best-effort: the boolean flags table above is the important part of
      // this page, and its own error state already covers "dev tools are
      // having trouble right now."
    }
  };

  useEffect(() => {
    load();
    loadVariants();
  }, []);

  const toggle = async (flag: Flag) => {
    setBusy(flag.name);
    try {
      const res = await fetch(`${apiUrl}/api/dev/flags/${flag.name}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled: !flag.enabled }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const updated: Flag = await res.json();
      setFlags((current) =>
        (current ?? []).map((f) => (f.name === updated.name ? updated : f)),
      );
      setError(null);
    } catch (err) {
      setError(`Could not set ${flag.name}: ${err}`);
    } finally {
      setBusy(null);
    }
  };

  const chooseVariant = async (flag: VariantFlag, value: string) => {
    setBusy(flag.name);
    try {
      const res = await fetch(`${apiUrl}/api/dev/variant-flags/${flag.name}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ value }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const updated: VariantFlag = await res.json();
      setVariantFlags((current) =>
        (current ?? []).map((f) => (f.name === updated.name ? updated : f)),
      );
      setError(null);
    } catch (err) {
      setError(`Could not set ${flag.name}: ${err}`);
    } finally {
      setBusy(null);
    }
  };

  const reset = async () => {
    setBusy("__reset__");
    try {
      const res = await fetch(`${apiUrl}/api/dev/flags/reset`, { method: "POST" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setFlags(await res.json());
      // The backend resets variant flags too (one button, one "defaults"),
      // so re-fetch rather than guess what they reset to.
      loadVariants();
      setError(null);
    } catch (err) {
      setError(`Could not reset: ${err}`);
    } finally {
      setBusy(null);
    }
  };

  if (unavailable) {
    return (
      <article>
        <h2>Feature flags</h2>
        <p>
          Dev tools are not available in this environment. Flags here are
          served by PostHog.
        </p>
      </article>
    );
  }

  const nothingDeclared = flags?.length === 0 && variantFlags?.length === 0;

  return (
    <article>
      <h2>Feature flags</h2>
      <p>
        <small>
          This environment ignores PostHog. Changes take effect immediately and
          persist across deploys.
        </small>
      </p>

      {error && (
        <p style={{ color: "var(--pico-del-color, #b3261e)" }}>{error}</p>
      )}

      {flags === null && !error && <p aria-busy="true">Loading…</p>}

      {nothingDeclared && <p>No feature flags are declared.</p>}

      {flags && !nothingDeclared && (
        <>
          <table>
            <thead>
              <tr>
                <th scope="col">Flag</th>
                <th scope="col">Value</th>
              </tr>
            </thead>
            <tbody>
              {flags.map((flag) => (
                <tr key={flag.name}>
                  <td>
                    <code>{flag.name}</code>
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      role="switch"
                      checked={flag.enabled}
                      disabled={busy !== null}
                      onChange={() => toggle(flag)}
                      aria-label={`Toggle ${flag.name}`}
                    />
                  </td>
                </tr>
              ))}
              {variantFlags?.map((flag) => (
                <tr key={flag.name}>
                  <td>
                    <code>{flag.name}</code>
                  </td>
                  <td>
                    <select
                      value={flag.value}
                      disabled={busy !== null}
                      onChange={(e) => chooseVariant(flag, e.target.value)}
                      aria-label={`Set ${flag.name}`}
                      style={{ width: "auto", marginBottom: 0 }}
                    >
                      {flag.variants.map((variant) => (
                        <option key={variant} value={variant}>
                          {variant}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <button
            className="secondary"
            onClick={reset}
            disabled={busy !== null}
          >
            Reset all to defaults
          </button>
        </>
      )}
    </article>
  );
}
